/* AI-authored (Claude) — maintained by AI, not hand-edited. */
import { debounce, FocusTrapUtils, onClick, onClick$, onInput, onSubmit, progressInstance } from "@nx-paeonia/utils-events";
import { sectionFetch } from "@nx-paeonia/utils-http";
import { isErr } from "@nx-paeonia/utils-result";
import { resolveShopifyUrl } from "@nx-paeonia/utils-shopify";
import { signal } from "@nx-paeonia/utils-signals";
import { clearResults, hideSearchOverlay, showSearchOverlay, showSearchOverlayItems } from "./helpers/search-overlay.helper.js";

const config = {
	resourceTypes: ["product", "query", "collection", "article"],
	limit: 4,
	limitScope: "each",
};

const isOpen = signal(false);
const overlay = document.querySelector<HTMLDialogElement>(".search-overlay");
if (!overlay) {
	console.warn("Search overlay dialog not found");
}

/**
 * There can be two search inputs on the page: the persistent field in the header
 * (desktop) and the field inside the overlay bar (mobile). Both carry
 * `js-search-input` and drive the same panel. `activeInput()` returns the one the
 * visitor is currently using (i.e. the visible one).
 */
function getInputs(): HTMLInputElement[] {
	return Array.from(document.querySelectorAll<HTMLInputElement>(".js-search-input"));
}

function activeInput(): HTMLInputElement | null {
	const inputs = getInputs();
	return inputs.find((el) => el.getClientRects().length > 0) ?? inputs[0] ?? null;
}

// Capture the idle content (trending tags) so we can restore it after clearing.
const idleHTML = overlay?.querySelector(".search-overlay__scrollbar-container")?.innerHTML ?? "";

function restoreIdle() {
	const container = overlay?.querySelector(".search-overlay__scrollbar-container");
	if (container && idleHTML) container.innerHTML = idleHTML;
}

/* -------------------------------------------------------------------------- */
/* Keyboard navigation (roving highlight over the result links)               */
/* -------------------------------------------------------------------------- */

let activeIndex = -1;

function getOptions(): HTMLAnchorElement[] {
	return Array.from(overlay?.querySelectorAll<HTMLAnchorElement>(".js-search-option") ?? []);
}

function clearActive() {
	activeIndex = -1;
	getOptions().forEach((el) => {
		el.classList.remove("is-active");
		el.setAttribute("aria-selected", "false");
	});
	getInputs().forEach((el) => el.removeAttribute("aria-activedescendant"));
}

function setActive(index: number) {
	const options = getOptions();
	if (options.length === 0) return;

	activeIndex = (index + options.length) % options.length;

	options.forEach((el, i) => {
		const on = i === activeIndex;
		el.classList.toggle("is-active", on);
		el.setAttribute("aria-selected", on ? "true" : "false");
		if (on) {
			if (!el.id) el.id = `search-option-${i}`;
			activeInput()?.setAttribute("aria-activedescendant", el.id);
			el.scrollIntoView({ block: "nearest" });
		}
	});
}

function setExpanded(expanded: boolean) {
	getInputs().forEach((el) => el.setAttribute("aria-expanded", expanded ? "true" : "false"));
}

/** Toggle the dimming backdrop: only dim once the visitor is actually searching. */
function setSearching(active: boolean) {
	overlay?.classList.toggle("is-searching", active);
}

function handleKeydown(event: KeyboardEvent) {
	const options = getOptions();

	switch (event.key) {
		case "ArrowDown":
			if (options.length === 0) return;
			event.preventDefault();
			setActive(activeIndex + 1);
			break;
		case "ArrowUp":
			if (options.length === 0) return;
			event.preventDefault();
			setActive(activeIndex - 1);
			break;
		case "Enter":
			// Only intercept when an option is highlighted; otherwise let the form
			// submit and navigate to the full results page.
			if (activeIndex >= 0 && options[activeIndex]) {
				event.preventDefault();
				window.location.href = options[activeIndex].href;
			}
			break;
		case "Escape":
			event.preventDefault();
			isOpen.update(() => false);
			break;
	}
}

/* -------------------------------------------------------------------------- */
/* Wire up every search input (header + overlay bar)                          */
/* -------------------------------------------------------------------------- */

getInputs().forEach((el) => {
	// Focusing a field opens the panel.
	el.addEventListener("focus", () => {
		if (overlay && !overlay.open) isOpen.update(() => true);
	});

	// Show/hide the in-field clear button immediately; reopen if closed while typing.
	el.addEventListener("input", () => {
		el.closest(".search-field")?.classList.toggle("has-query", el.value.length > 0);
		if (el.value.length > 0 && overlay && !overlay.open) isOpen.update(() => true);
	});

	el.addEventListener("keydown", handleKeydown);
});

/* -------------------------------------------------------------------------- */
/* Open / close                                                               */
/* -------------------------------------------------------------------------- */

if (overlay) {
	const focusTrap = FocusTrapUtils.createModalTrap(overlay, {
		blockScroll: true,
		initialFocus: activeInput() ?? undefined,
		returnFocus: document.querySelector<HTMLElement>('[data-target=".search-overlay"]') ?? undefined,
	});

	isOpen.subscribe((open) => {
		document.body.classList.toggle("search-open", open);
		if (open) {
			showSearchOverlay();
			focusTrap.activate();
		} else {
			hideSearchOverlay();
			focusTrap.deactivate();
			clearActive();
			setExpanded(false);
			setSearching(false);
		}
	});
}

// Mobile: the header search icon toggles the overlay.
onClick(
	'[data-target=".search-overlay"]',
	() => {
		isOpen.update((c) => !c);
	},
	{ preventDefault: true },
);

onClick(".search-overlay .backdrop", () => {
	isOpen.update(() => false);
});

onClick(".search-overlay__close", () => {
	isOpen.update(() => false);
});

// Desktop has no close button (the field lives in the header), so close when the
// visitor clicks anywhere outside the panel and the search fields.
document.addEventListener("click", (event) => {
	if (!overlay?.open) return;
	const target = event.target as HTMLElement;
	if (target.closest(".search-overlay") || target.closest(".search-field") || target.closest('[data-target=".search-overlay"]')) {
		return;
	}
	isOpen.update(() => false);
});

// Clear button lives inside each (static) field, so a direct listener is fine.
onClick(".js-search-clear", (_event, element) => {
	const field = element.closest<HTMLElement>(".search-field");
	const inp = field?.querySelector<HTMLInputElement>(".js-search-input");
	if (inp) {
		inp.value = "";
		inp.focus();
	}
	field?.classList.remove("has-query");
	clearResults();
	restoreIdle();
	clearActive();
	setExpanded(false);
	setSearching(false);
});

/* -------------------------------------------------------------------------- */
/* Live search                                                                */
/* -------------------------------------------------------------------------- */

const debouncedInputCallback = debounce(
	async (_evt, element) => {
		clearActive();

		const field = (element as HTMLInputElement | null) ?? activeInput();
		const value = field?.value ?? "";

		const baseUrl = resolveShopifyUrl("search", "suggest");
		const searchParams = new URLSearchParams();
		searchParams.set("q", value);
		searchParams.set("resources[type]", config.resourceTypes.join(","));
		searchParams.set("resources[limit]", config.limit.toString());
		searchParams.set("resources[limit_scope]", config.limitScope);
		searchParams.set("section_id", "search-overlay");

		const url = `${baseUrl}?${searchParams.toString()}`;

		progressInstance.start();

		if (value.length === 0) {
			clearResults();
			restoreIdle();
			setExpanded(false);
			setSearching(false);
			progressInstance.done(true);
			return;
		}

		if (overlay) {
			const target = overlay.querySelector<HTMLElement>(".search-overlay__scrollbar-container");

			const section = await sectionFetch(url.toString());

			if (isErr(section)) {
				clearResults();
				setExpanded(false);
				return;
			}

			if (!target) {
				console.warn("Search results wrapper not found");
				return;
			}

			if (section.data.firstElementChild) {
				target.replaceWith(section.data.firstElementChild);
			}

			progressInstance.done(true);

			showSearchOverlayItems();
			setExpanded(true);
			setSearching(true);
		}
	},
	300,
	{ leading: false, trailing: true },
);

onInput(".js-search-input", debouncedInputCallback, { preventDefault: true });

// Trending tags (idle state): clicking one runs that search. Delegated so it keeps
// working after the idle content is restored.
onClick$(
	".js-search-tag",
	(_event, element) => {
		const term = element.dataset.term ?? element.textContent?.trim() ?? "";
		const inp = activeInput();
		if (!inp || term.length === 0) return;
		inp.value = term;
		inp.closest(".search-field")?.classList.add("has-query");
		if (overlay && !overlay.open) isOpen.update(() => true);
		inp.focus();
		debouncedInputCallback(null, inp);
	},
	{ preventDefault: true },
);

onSubmit(
	".search-field",
	(_event, element) => {
		// Enter without a highlighted option -> go to the full results page.
		const inp = element.querySelector<HTMLInputElement>(".js-search-input");
		const term = inp?.value?.trim() ?? "";
		if (term.length === 0) return;
		const searchUrl = resolveShopifyUrl("search");
		const params = new URLSearchParams({ q: term, type: "product,article" });
		window.location.href = `${searchUrl}?${params.toString()}`;
	},
	{ preventDefault: true },
);
